import { useMemo, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { Icon } from "../../../components/Icon";
import { PageHead } from "../../../components/ui";
import { useAuslassungTitel } from "../../../lib/auslassung";
import { t, ta } from "../../../lib/sprache";
import {
  STAND_TON, STAND_ZEICHEN, filtereHub, hubKarten, istBlick, kartenPill, standardOffen, type Blick, type HubKarte, type HubKoop, type Stand,
} from "./hub-model";
import "./hub.css";

/**
 * Darstellung des Hubs für beide Portale: Seitenkopf mit Umschalter der Blickwinkel, Suche, Zähler „n hängen“, Filter
 * „Nur aktive“, Legende und je Gruppe eine aufklappbare Karte mit einer Zeile je Kooperation (drei Stand-Pillen).
 * Zustand steht in der Adresse (?ansicht=, ?q=, ?erledigte=1, ?haengt=1), damit Zurück und geteilte Links dieselbe Sicht zeigen.
 */

const BLICK_LABEL: Record<Blick, () => string> = {
  marke: () => t("Nach Marke", "By brand"),
  creator: () => t("Nach Creator", "By creator"),
  kampagne: () => t("Nach Kampagne", "By campaign"),
};

type Art = "vertrag" | "content" | "rechnung";
const ARTEN: readonly Art[] = ["vertrag", "content", "rechnung"];
const ART_LABEL: Record<Art, () => string> = {
  vertrag: () => t("Vertrag", "Contract"),
  content: () => t("Content", "Content"),
  rechnung: () => t("Rechnung", "Invoice"),
};

export function HubAnsicht({ koops, blicke, leer, titel, text, eyebrow, aktionen, fuss, hinweis, kartenOptionen, suchfeld, klasse }: {
  koops: readonly HubKoop[];
  blicke: readonly Blick[];
  /** Leerer Zustand, wenn es keine einzige Kooperation gibt */
  leer: ReactNode;
  titel: string;
  text: string;
  eyebrow?: string;
  aktionen?: ReactNode;
  /** Unter dem Inhalt, z. B. der Demo-Hinweis */
  fuss?: ReactNode;
  /** Satz unter den Karten (Standard: Klick auf Zeile bzw. Pille) */
  hinweis?: string;
  /** Creator-Portal: ohne Kampagnen, „Deals“ statt „Kooperationen“ */
  kartenOptionen?: Parameters<typeof hubKarten>[2];
  /** Beschriftung und Platzhalter der Suche (Standard: Marke, Creator, Kampagne, Kooperation) */
  suchfeld?: { label: string; platzhalter: string };
  /** Zusätzliche Seitenklassen (Creator-Portal: tk-cr) */
  klasse?: string;
}) {
  const [params, setParams] = useSearchParams();
  const roh = params.get("ansicht");
  const blick: Blick = istBlick(roh) && blicke.includes(roh) ? roh : blicke[0]!;
  const suche = params.get("q") ?? "";
  const nurAktiv = params.get("erledigte") !== "1";
  const nurHaengend = params.get("haengt") === "1";
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const { koops: gezeigt, haengend } = useMemo(() => filtereHub(koops, { suche, nurAktiv, nurHaengend }), [koops, suche, nurAktiv, nurHaengend]);
  const karten = useMemo(() => hubKarten(gezeigt, blick, kartenOptionen), [gezeigt, blick, kartenOptionen]);
  const [umgeschaltet, setUmgeschaltet] = useState<Record<string, boolean>>({});
  const offen = (k: HubKarte) => umgeschaltet[k.key] ?? standardOffen(k, karten.length, suche);
  const alleOffen = karten.length > 0 && karten.every(offen);
  const ellRef = useAuslassungTitel<HTMLDivElement>();
  // „Deals“ statt „Kooperationen“: im Creator-Konto (ta) und im Creator-Portal (kartenOptionen.deals)
  const deals = !!kartenOptionen?.deals;

  return (
    <main id="main" className={klasse ? `content tk-hub ${klasse}` : "content tk-hub"}>
      <PageHead eyebrow={eyebrow} title={titel} text={text}>
        {blicke.length > 1 && (
          <div className="tk-ansicht" role="group" aria-label={t("Blickwinkel", "Perspective")}>
            {blicke.map((b) => (
              <button key={b} type="button" className={b === blick ? "btn primary" : "btn"} aria-pressed={b === blick} onClick={() => setParam("ansicht", b === blicke[0] ? "" : b)}>
                {BLICK_LABEL[b]()}
              </button>
            ))}
          </div>
        )}
        {aktionen}
      </PageHead>

      {koops.length === 0 ? leer : (
        <>
          <div className="toolbar tk-hub-leiste">
            <input type="search" aria-label={suchfeld?.label ?? t("Marke, Creator, Kampagne oder Kooperation suchen", "Search brand, creator, campaign or deal")}
              placeholder={suchfeld?.platzhalter ?? t("Marke, Creator, Kampagne, Kooperation …", "Brand, creator, campaign, deal …")} value={suche} onChange={(e) => setParam("q", e.target.value)} />
            {/* Zähler und Filter als eigene Gruppe: direkte Chips der Werkzeugleiste rutschen sonst in eine zweite Zeile (original.css) */}
            <span className="tk-hub-filter">
              <button type="button" className="tk-hub-zaehler" aria-pressed={nurHaengend} disabled={!haengend && !nurHaengend} onClick={() => setParam("haengt", nurHaengend ? "" : "1")}
                title={deals ? t("Nur Deals zeigen, bei denen etwas hängt", "Only show deals where something is stuck") : ta("Nur Kooperationen zeigen, bei denen etwas hängt", "Only show deals where something is stuck", "Nur Deals zeigen, bei denen etwas hängt", "Only show deals where something is stuck")}>
                <span className="tk-hub-zeichen" aria-hidden="true">!</span>
                {haengend === 1 ? t("1 hängt", "1 stuck") : t(`${haengend} hängen`, `${haengend} stuck`)}
              </button>
              <label className="tk-hub-schalter">
                <input type="checkbox" checked={nurAktiv} onChange={(e) => setParam("erledigte", e.target.checked ? "" : "1")} />
                {t("Nur aktive", "Active only")}
              </label>
            </span>
            {karten.length > 1 && (
              <button type="button" className="tk-hub-alle" onClick={() => setUmgeschaltet(Object.fromEntries(karten.map((k) => [k.key, !alleOffen])))}>
                {alleOffen ? t("Alle zuklappen", "Collapse all") : t("Alle aufklappen", "Expand all")}
              </button>
            )}
            <Legende />
          </div>

          {karten.length === 0 ? (
            <div className="panel stack">
              <p>{nurHaengend ? t("Gerade hängt nichts. Alles im Plan.", "Nothing is stuck right now. All on track.") : deals ? t("Keine passenden Deals. Passe die Suche oder die Filter an.", "No matching deals. Adjust the search or the filters.") : ta("Keine passenden Kooperationen. Passe die Suche oder die Filter an.", "No matching deals. Adjust the search or the filters.", "Keine passenden Deals. Passe die Suche oder die Filter an.", "No matching deals. Adjust the search or the filters.")}</p>
              <div className="row"><button type="button" onClick={() => setParams(blick === blicke[0] ? new URLSearchParams() : new URLSearchParams({ ansicht: blick }), { replace: true })}>{t("Filter zurücksetzen", "Reset filters")}</button></div>
            </div>
          ) : (
            <div className="tk-hub-karten" ref={ellRef}>
              {karten.map((k) => <Karte key={k.key} karte={k} offen={offen(k)} umschalten={() => setUmgeschaltet((u) => ({ ...u, [k.key]: !offen(k) }))} />)}
            </div>
          )}
          <p className="source-note">{hinweis ?? ta("Klick auf eine Zeile öffnet die Kooperation, Klick auf eine Pille direkt den Vertrag, den Content oder die Rechnung.", "Click a row to open the deal, click a pill to go straight to the contract, the content or the invoice.",
            "Klick auf eine Zeile öffnet den Deal, Klick auf eine Pille direkt den Vertrag, den Content oder die Rechnung.", "Click a row to open the deal, click a pill to go straight to the contract, the content or the invoice.")}</p>
        </>
      )}
      {fuss}
    </main>
  );
}

function Legende() {
  const eintrag = (art: Stand["art"], text: string) => (
    <li><span className={`tk-hub-zeichen ${STAND_TON[art]}`} aria-hidden="true">{STAND_ZEICHEN[art]}</span>{text}</li>
  );
  return (
    <ul className="tk-hub-legende" aria-label={t("Legende", "Legend")}>
      {eintrag("ok", t("erledigt", "done"))}
      {eintrag("laeuft", t("offen", "open"))}
      {eintrag("haengt", t("hängt", "stuck"))}
      {eintrag("leer", t("noch nicht dran", "not yet due"))}
    </ul>
  );
}

function Karte({ karte, offen, umschalten }: { karte: HubKarte; offen: boolean; umschalten: () => void }) {
  const kopf = kartenPill(karte);
  const id = `hub-${karte.key.replace(/[^a-zA-Z0-9-]/g, "-")}`;
  return (
    <section className="panel tk-hub-karte" aria-labelledby={`${id}-titel`}>
      <h2 className="tk-hub-h">
        <button type="button" className="tk-hub-kopf" aria-expanded={offen} aria-controls={id} onClick={umschalten}>
          <span className={karte.rund ? "avatar tk-hub-avatar rund" : "avatar tk-hub-avatar"} aria-hidden="true">{karte.initialen}</span>
          <span className="tk-hub-kopftext">
            <strong id={`${id}-titel`} className={karte.ohneKampagne ? "tk-ell tk-hub-ohne" : "tk-ell"}>{karte.titel}</strong>
            <small className="tk-ell">{karte.unter}</small>
          </span>
          <span className={`pill ${kopf.tone}`}><span className="pill-zeichen">{kopf.symbol}</span> {kopf.label}</span>
          <Icon name="chevron" className="icon tk-hub-auf" />
        </button>
      </h2>
      {offen && (
        <div id={id} className="tk-hub-zeilen">
          <div className="tk-hub-spalten" aria-hidden="true">
            <span />
            {ARTEN.map((a) => <span key={a}>{ART_LABEL[a]()}</span>)}
            <span />
          </div>
          <ul className="tk-hub-liste">
            {karte.zeilen.map((z) => (
              <li key={z.koop.id} className="tk-hub-zeile">
                <Link className="tk-hub-ziel" to={z.koop.href}>
                  <strong className="tk-ell">{z.haupt}</strong>
                  {z.neben && <small className="tk-ell">{z.neben}</small>}
                </Link>
                <span className="tk-hub-pillen">
                  {ARTEN.map((a) => <StandPille key={a} art={a} s={z.koop[a]} href={z.koop.href} />)}
                </span>
                <Icon name="chevron" className="icon tk-hub-pfeil" />
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function StandPille({ art, s, href }: { art: Art; s: Stand; href: string }) {
  const name = ART_LABEL[art]();
  return (
    <Link className={`pill ${STAND_TON[s.art]} tk-hub-pille`} data-art={art} to={s.ziel ?? href}
      aria-label={`${name}: ${s.label}`} title={`${name}: ${s.label}`}>
      <span className="tk-hub-art">{name}</span>
      <span aria-hidden="true">{STAND_ZEICHEN[s.art]}</span>
      <span className="tk-hub-pillentext">{s.label}</span>
    </Link>
  );
}
